import React, { useContext, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  LinearProgress,
  Skeleton,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useNavigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { OverviewAPI } from 'api';
import { JobTableType } from 'api/jobApi';
import { buildJobListUrl } from '../jobListUrlParams';
import UserContext from 'state/UserContext';
import useIsMobile from 'theme/useIsMobile';
import { ACTIVITY_BUCKETS } from 'constants/activityBuckets';
import {
  cardSx,
  chartTokens,
  columnHeaderSx,
  footerSx,
  formatCount,
  getInitials,
  subtitleSx,
  tabularNumSx,
  titleSx,
} from './dashboardChartTokens';

/** Coordinators listed before the "See all coordinators" link is used. */
const COLLAPSED_ROW_COUNT = 10;

/**
 * The four activity buckets, in display order, with the colour and label each
 * segment of the bar uses. Sourced from `ACTIVITY_BUCKETS` so the chart and the
 * jobs table filter agree by construction.
 */
const SEGMENTS = ACTIVITY_BUCKETS.map((bucket) => ({
  key: bucket.key,
  label: bucket.shortLabel,
  color: bucket.accentColor,
}));

type SegmentKey = (typeof SEGMENTS)[number]['key'];

interface HealthRow {
  userId: string;
  name: string;
  initials: string;
  isCurrentUser: boolean;
  total: number;
  segments: Record<SegmentKey, number>;
}

const HealthBar = ({
  row,
  scaleMax,
  onSegmentClick,
}: {
  row: HealthRow;
  scaleMax: number;
  /** Drill-through for one activity bucket of this coordinator's bar. */
  onSegmentClick?: (bucket: SegmentKey) => void;
}) => {
  const widthOf = (count: number) =>
    scaleMax > 0 ? (count / scaleMax) * 100 : 0;

  return (
    <Box
      sx={{
        position: 'relative',
        height: 26,
        borderRadius: '6px',
        backgroundColor: chartTokens.track,
        overflow: 'hidden',
        display: 'flex',
      }}>
      {SEGMENTS.map((segment, index) => {
        const count = row.segments[segment.key];
        if (count <= 0) return null;
        const width = widthOf(count);
        return (
          <Tooltip
            key={segment.key}
            title={`${segment.label}: ${formatCount(count)}${
              onSegmentClick ? ' — click to view jobs' : ''
            }`}>
            <Box
              onClick={
                onSegmentClick
                  ? (event) => {
                      // Keep the row-level drill-through from also firing.
                      event.stopPropagation();
                      onSegmentClick(segment.key);
                    }
                  : undefined
              }
              sx={{
                height: '100%',
                width: `${width}%`,
                minWidth: count > 0 ? 3 : 0,
                backgroundColor: segment.color,
                // Round only the ends of the bar, not the internal joins.
                borderTopLeftRadius: index === 0 ? '6px' : 0,
                borderBottomLeftRadius: index === 0 ? '6px' : 0,
                borderTopRightRadius:
                  index === SEGMENTS.length - 1 ? '6px' : 0,
                borderBottomRightRadius:
                  index === SEGMENTS.length - 1 ? '6px' : 0,
                transition: 'width 300ms ease',
                cursor: onSegmentClick ? 'pointer' : 'default',
                '&:hover': onSegmentClick ? { opacity: 0.8 } : undefined,
              }}
            />
          </Tooltip>
        );
      })}
    </Box>
  );
};

const Legend = ({
  onBucketClick,
}: {
  /** Drill-through for one activity bucket across all coordinators. */
  onBucketClick?: (bucket: SegmentKey) => void;
}) => (
  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, marginTop: 2 }}>
    {SEGMENTS.map((segment) => (
      <Box
        key={segment.key}
        onClick={onBucketClick ? () => onBucketClick(segment.key) : undefined}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          cursor: onBucketClick ? 'pointer' : 'default',
          borderRadius: '6px',
          transition: 'opacity 150ms ease',
          '&:hover': onBucketClick ? { opacity: 0.7 } : undefined,
        }}>
        <Box
          sx={{
            width: 10,
            height: 10,
            borderRadius: '3px',
            backgroundColor: segment.color,
          }}
        />
        <Typography sx={{ fontSize: 12.5, color: chartTokens.body }}>
          {segment.label}
        </Typography>
      </Box>
    ))}
  </Box>
);

const JobHealthByCoordinatorChart = () => {
  const navigate = useNavigate();
  const isMobile = useIsMobile('xs');
  const { user } = useContext(UserContext);
  const [showAll, setShowAll] = useState(false);

  /**
   * Drill-throughs. Row -> that coordinator's open jobs; bar segment ->
   * additionally filtered by the clicked activity bucket, which is the same
   * key the job list's "Time Since Last Active" filter uses. Legend chip ->
   * one bucket across every coordinator.
   */
  const drillToCoordinatorJobs = (userId: string) =>
    navigate(
      buildJobListUrl({ tab: JobTableType.OPEN, coordinator: [userId] })
    );

  const drillToBucketJobs = (userId: string, bucket: SegmentKey) =>
    navigate(
      buildJobListUrl({
        tab: JobTableType.OPEN,
        activity: [bucket],
        coordinator: [userId],
      })
    );

  const drillToBucketJobsForAll = (bucket: SegmentKey) =>
    navigate(
      buildJobListUrl({ tab: JobTableType.OPEN, activity: [bucket] })
    );

  // No period selector: the activity buckets are inherently relative to
  // "now", so the endpoint takes no date range.
  const { isFetching, data } = useQuery(
    ['job-health-by-coordinator'],
    () => OverviewAPI.fetchJobHealthByCoordinator()
  );

  const rows: HealthRow[] = useMemo(() => {
    const coordinators = data?.coordinators ?? [];
    return coordinators
      .map((coordinator) => ({
        userId: `${coordinator.userId}`,
        name: `${coordinator.firstName} ${coordinator.lastName}`.trim(),
        initials: getInitials(coordinator.firstName, coordinator.lastName),
        isCurrentUser: `${coordinator.userId}` === `${user?.id}`,
        total: coordinator.total,
        segments: {
          H00_24: coordinator.active,
          H24_48: coordinator.slowingDown,
          H48_120: coordinator.stalled,
          H120_PLUS: coordinator.criticallyStalled,
        },
      }))
      .sort((a, b) => b.total - a.total);
  }, [data, user?.id]);

  const visibleRows = showAll ? rows : rows.slice(0, COLLAPSED_ROW_COUNT);
  const totalJobs = visibleRows.reduce((total, row) => total + row.total, 0);
  const scaleMax = Math.max(...rows.map((row) => row.total), 1);

  return (
    <Card sx={cardSx}>
      {isFetching && <LinearProgress />}
      <CardContent sx={{ padding: { xs: 2.5, md: 3 }, paddingBottom: 0 }}>
        <Box>
          <Typography sx={titleSx}>Job Health by Coordinator</Typography>
          <Typography sx={subtitleSx}>
            {`${formatCount(totalJobs)} open jobs, highest workload first · activity is measured against the current time`}
          </Typography>
        </Box>
        <Legend onBucketClick={drillToBucketJobsForAll} />
      </CardContent>

      <Divider sx={{ borderColor: chartTokens.divider, marginTop: 2 }} />

      <CardContent
        sx={{
          flex: 1,
          padding: { xs: 2.5, md: 3 },
          paddingTop: { xs: 2, md: 2 },
        }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: isMobile
              ? 'minmax(0, 1fr) 48px'
              : 'minmax(0, 220px) minmax(0, 1fr) 56px',
            alignItems: 'center',
            columnGap: 2,
            paddingBottom: 1.5,
          }}>
          <Typography sx={columnHeaderSx}>Project Coordinator</Typography>
          {!isMobile && <Typography sx={columnHeaderSx}>Health</Typography>}
          <Typography sx={{ ...columnHeaderSx, textAlign: 'right' }}>
            Jobs
          </Typography>
        </Box>
        <Divider sx={{ borderColor: chartTokens.divider }} />

        {isFetching && !rows.length
          ? Array.from({ length: 6 }).map((_, index) => (
              <Skeleton key={index} height={52} />
            ))
          : visibleRows.map((row) => (
              <Box
                key={row.userId}
                role="button"
                tabIndex={0}
                onClick={() => drillToCoordinatorJobs(row.userId)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    drillToCoordinatorJobs(row.userId);
                  }
                }}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: isMobile
                    ? 'minmax(0, 1fr) 48px'
                    : 'minmax(0, 220px) minmax(0, 1fr) 56px',
                  alignItems: 'center',
                  columnGap: 2,
                  paddingY: 1.25,
                  borderBottom: `1px solid ${chartTokens.divider}`,
                  cursor: 'pointer',
                  transition: 'background-color 150ms ease',
                  '&:hover': { backgroundColor: chartTokens.accentMuted },
                }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.25,
                    minWidth: 0,
                  }}>
                  <Box
                    sx={{
                      flexShrink: 0,
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 12,
                      fontWeight: 700,
                      backgroundColor: row.isCurrentUser
                        ? chartTokens.accent
                        : chartTokens.accentSoft,
                      color: row.isCurrentUser ? '#FFFFFF' : chartTokens.accent,
                    }}>
                    {row.initials}
                  </Box>
                  <Tooltip title={row.name}>
                    <Typography
                      noWrap
                      sx={{
                        fontSize: 14.5,
                        fontWeight: row.isCurrentUser ? 700 : 500,
                        color: chartTokens.heading,
                      }}>
                      {row.name}
                    </Typography>
                  </Tooltip>
                  {row.isCurrentUser && (
                    <Typography
                      component="span"
                      sx={{
                        flexShrink: 0,
                        fontSize: 11,
                        fontWeight: 700,
                        letterSpacing: '0.06em',
                        color: chartTokens.accent,
                      }}>
                      YOU
                    </Typography>
                  )}
                </Box>

                {!isMobile && (
                  <HealthBar
                    row={row}
                    scaleMax={scaleMax}
                    onSegmentClick={(bucket) =>
                      drillToBucketJobs(row.userId, bucket)
                    }
                  />
                )}

                <Typography
                  sx={{
                    fontSize: 15,
                    fontWeight: 700,
                    textAlign: 'right',
                    color: chartTokens.heading,
                    ...tabularNumSx,
                  }}>
                  {formatCount(row.total)}
                </Typography>
              </Box>
            ))}

        {!isFetching && !rows.length && (
          <Typography sx={{ ...footerSx, paddingY: 3, textAlign: 'center' }}>
            No coordinator activity to show for this period.
          </Typography>
        )}

        {Boolean(rows.length) && (
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1,
              alignItems: 'center',
              justifyContent: 'space-between',
              paddingTop: 2,
            }}>
            <Typography sx={footerSx}>
              {`Showing ${visibleRows.length} of ${rows.length} coordinator${
                rows.length === 1 ? '' : 's'
              } · bars are relative to the busiest coordinator`}
            </Typography>
            {rows.length > COLLAPSED_ROW_COUNT && (
              <Button
                disableRipple
                endIcon={<ArrowForwardIcon sx={{ fontSize: 16 }} />}
                onClick={() => setShowAll((current) => !current)}
                sx={{
                  textTransform: 'none',
                  fontSize: 13.5,
                  fontWeight: 600,
                  color: chartTokens.accent,
                  padding: 0,
                  minWidth: 0,
                  '&:hover': { backgroundColor: 'transparent' },
                }}>
                {showAll ? 'Show top 10' : 'See all coordinators'}
              </Button>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default JobHealthByCoordinatorChart;
